import React, { useState } from 'react';
import { 
  BarChart, Bar, ScatterChart, Scatter, XAxis, YAxis, 
  CartesianGrid, Tooltip, Legend, ResponsiveContainer, ZAxis, Cell 
} from 'recharts';

// Import sample data
import { shops, shopCategoriesL1, shopCategoriesL2, productDistributionByShopType } from '../../data/shopData';
import { productCategoriesL1 } from '../../data/productData';
import { countries, regions } from '../../data/geoData';

const ShopPerformance = () => {
  const [selectedShopCategory, setSelectedShopCategory] = useState('all');
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [selectedMetric, setSelectedMetric] = useState('totalSales');
  const [sortBy, setSortBy] = useState('totalSales');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Filter shops based on selected criteria
  const getFilteredShops = () => {
    return shops.filter(shop => {
      // Filter by shop category if selected
      if (selectedShopCategory !== 'all') {
        const shopCategoryL2 = shopCategoriesL2.find(cat => cat.id === shop.categoryL2Id);
        if (!shopCategoryL2) return false;
        
        const shopCategoryL1 = shopCategoriesL1.find(cat => cat.id === shopCategoryL2.parentId);
        if (!shopCategoryL1 || shopCategoryL1.id !== parseInt(selectedShopCategory)) return false;
      }
      
      // Filter by region if selected
      if (selectedRegion !== 'all') {
        // In a real app, we would need to join with location data
        // For the POC, we'll just return all shops when filtering by region
        return true;
      }
      
      return true;
    });
  };
  
  // Sort shops based on selected criteria
  const getSortedShops = () => {
    const filtered = getFilteredShops();
    
    return [...filtered].sort((a, b) => {
      const valueA = a[sortBy];
      const valueB = b[sortBy];
      
      if (sortDirection === 'asc') {
        return valueA - valueB;
      } else {
        return valueB - valueA;
      }
    });
  };
  
  // Toggle sort direction
  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('desc');
    }
  };
  
  // Get shop category name by ID
  const getShopCategoryName = (categoryL2Id) => {
    const category = shopCategoriesL2.find(cat => cat.id === categoryL2Id);
    return category ? category.name : 'Unknown';
  };
  
  // Prepare data for the scatter plot
  const prepareScatterData = () => {
    return getFilteredShops().map(shop => ({
      id: shop.id,
      name: shop.name,
      x: shop.totalSales / 1000000, // Convert to millions for better display
      y: shop.growth,
      z: shop.productCount / 5000, // Normalize size for better visual
      categoryL2Id: shop.categoryL2Id,
      category: getShopCategoryName(shop.categoryL2Id),
      locationType: shop.locationType
    }));
  };
  
  // Prepare category distribution data for bar chart
  const prepareCategoryDistributionData = () => {
    if (selectedShopCategory === 'all') {
      // Show average across all shop types
      const aggregated = productCategoriesL1.map(prodCat => {
        const totalPercentage = shopCategoriesL1.reduce((sum, shopCat) => {
          const distribution = productDistributionByShopType.find(
            d => d.shopCategoryL1Id === shopCat.id && d.productCategoryL1Id === prodCat.id
          );
          return sum + (distribution ? distribution.salesPercentage : 0);
        }, 0);
        
        const avgPercentage = totalPercentage / shopCategoriesL1.length;
        
        return {
          category: prodCat.name,
          percentage: avgPercentage
        };
      });
      
      return aggregated.sort((a, b) => b.percentage - a.percentage);
    } else {
      // Show for selected shop category
      const shopCatId = parseInt(selectedShopCategory);
      const filtered = productDistributionByShopType
        .filter(d => d.shopCategoryL1Id === shopCatId)
        .map(d => {
          const prodCat = productCategoriesL1.find(pc => pc.id === d.productCategoryL1Id);
          return {
            category: prodCat ? prodCat.name : `Category ${d.productCategoryL1Id}`,
            percentage: d.salesPercentage
          };
        });
      
      return filtered.sort((a, b) => b.percentage - a.percentage);
    }
  };
  
  // Custom tooltip for scatter chart
  const renderScatterTooltip = (props) => {
    const { active, payload } = props;
    
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      
      return (
        <div className="bg-white p-3 border border-gray-200 shadow-sm rounded-md">
          <p className="font-semibold">{data.name}</p>
          <p className="text-sm">Category: {data.category}</p>
          <p className="text-sm">Sales: ${data.x.toFixed(2)}M</p>
          <p className="text-sm">Growth: {data.y.toFixed(1)}%</p>
          <p className="text-sm">Products: {data.z * 5000}</p>
          <p className="text-sm">Type: {data.locationType}</p>
        </div>
      );
    }
    
    return null;
  };
  
  // Get custom color for scatter points based on shop type
  const getShopTypeColor = (type) => {
    switch(type) {
      case 'physical': return '#0088FE';
      case 'online': return '#00C49F';
      case 'hybrid': return '#FFBB28';
      default: return '#8884d8';
    }
  };
  
  return (
    <div className="bg-white p-6 rounded-lg shadow-sm">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">Shop Performance Analysis</h1>
        <p className="text-gray-600 mb-4">
          Analyze retail shop performance across different categories, regions, and metrics. Visualize performance distribution and product mix.
        </p>
        
        {/* Filter controls */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-3">Shop Category</label>
            <select 
              value={selectedShopCategory}
              onChange={(e) => setSelectedShopCategory(e.target.value)}
              className="dropdown-selector"
            >
              <option value="all">All Shop Categories</option>
              {shopCategoriesL1.map(category => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-3">Region</label>
            <select 
              value={selectedRegion}
              onChange={(e) => setSelectedRegion(e.target.value)}
              className="dropdown-selector"
            >
              <option value="all">All Regions</option>
              {regions.map(region => (
                <option key={region.id} value={region.id}>{region.name}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-3">Performance Metric</label>
            <select 
              value={selectedMetric}
              onChange={(e) => setSelectedMetric(e.target.value)}
              className="dropdown-selector"
            >
              <option value="totalSales">Total Sales (USD)</option>
              <option value="growth">Growth Rate (%)</option>
              <option value="productCount">Product Count</option>
            </select>
          </div>
        </div>
      </div>
      
      {/* Shop performance distribution visualization */}
      <div className="border border-gray-200 rounded-lg p-4 mb-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-3">Shop Performance Distribution</h2>
        <div className="h-96">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart
              margin={{ top: 20, right: 20, bottom: 10, left: 30 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                type="number" 
                dataKey="x" 
                name="Sales" 
                unit="M" 
                label={{ 
                  value: 'Sales (Millions USD)', 
                  position: 'bottom',
                  style: { textAnchor: 'middle' }
                }} 
              />
              <YAxis 
                type="number" 
                dataKey="y" 
                name="Growth" 
                unit="%" 
                label={{ 
                  value: 'Growth Rate (%)', 
                  angle: -90, 
                  position: 'left',
                  style: { textAnchor: 'middle' }
                }} 
              />
              <ZAxis 
                type="number" 
                dataKey="z" 
                range={[20, 500]} 
                name="Products" 
              />
              <Tooltip content={renderScatterTooltip} cursor={{ strokeDasharray: '3 3' }} />
              <Legend 
                payload={[
                  { value: 'Physical', type: 'circle', color: '#0088FE' },
                  { value: 'Online', type: 'circle', color: '#00C49F' },
                  { value: 'Hybrid', type: 'circle', color: '#FFBB28' }
                ]}
              />
              <Scatter name="Shops" data={prepareScatterData()}>
                {prepareScatterData().map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={getShopTypeColor(entry.locationType)} 
                  />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      {/* Product category distribution by shop type */}
      <div className="border border-gray-200 rounded-lg p-4 mb-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-3">
          Product Category Distribution
          {selectedShopCategory !== 'all' && ` (${shopCategoriesL1.find(c => c.id === parseInt(selectedShopCategory))?.name || 'Selected Category'})`}
        </h2>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={prepareCategoryDistributionData()}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" unit="%" />
              <YAxis type="category" dataKey="category" />
              <Tooltip formatter={(value) => `${value.toFixed(1)}%`} />
              <Legend />
              <Bar dataKey="percentage" fill="#0088FE" name="Sales Percentage" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      {/* Shop performance table */}
      <div>
        <h2 className="text-lg font-semibold text-gray-800 mb-3">Shop Performance Data</h2>
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Shop Name</th>
                <th>
                  <button 
                    onClick={() => toggleSort('categoryL2Id')}
                    className="flex items-center"
                  >
                    Category
                    {sortBy === 'categoryL2Id' && (
                      <span className="ml-1">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </button>
                </th>
                <th>
                  <button 
                    onClick={() => toggleSort('locationType')}
                    className="flex items-center"
                  >
                    Type
                    {sortBy === 'locationType' && (
                      <span className="ml-1">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </button>
                </th>
                <th>
                  <button 
                    onClick={() => toggleSort('totalSales')}
                    className="flex items-center"
                  >
                    Total Sales
                    {sortBy === 'totalSales' && (
                      <span className="ml-1">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </button>
                </th>
                <th>
                  <button 
                    onClick={() => toggleSort('growth')}
                    className="flex items-center"
                  >
                    Growth
                    {sortBy === 'growth' && (
                      <span className="ml-1">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </button>
                </th>
                <th>
                  <button 
                    onClick={() => toggleSort('productCount')}
                    className="flex items-center"
                  >
                    Products
                    {sortBy === 'productCount' && (
                      <span className="ml-1">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {getSortedShops().map(shop => (
                <tr key={shop.id}>
                  <td>{shop.name}</td>
                  <td>{getShopCategoryName(shop.categoryL2Id)}</td>
                  <td>
                    <span className={`inline-block px-2 py-1 text-xs font-semibold rounded-full 
                      ${shop.locationType === 'physical' ? 'bg-blue-100 text-blue-800' : 
                        shop.locationType === 'online' ? 'bg-green-100 text-green-800' : 
                        'bg-yellow-100 text-yellow-800'}`}
                    >
                      {shop.locationType}
                    </span>
                  </td>
                  <td>${(shop.totalSales / 1000000).toFixed(2)}M</td>
                  <td className={shop.growth >= 0 ? 'positive-growth' : 'negative-growth'}>
                    {shop.growth >= 0 ? '↑' : '↓'} {Math.abs(shop.growth).toFixed(1)}%
                  </td>
                  <td>{shop.productCount.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ShopPerformance;